import { useEffect, useMemo, useState } from "react"
import { Link, NavLink } from "react-router-dom"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { Cinema } from "../../types/entities/cinema"
import { City } from "../../types/entities/city"
import { cinemaDisplayName } from "../../lib/cinemaDisplayName"
import { afishaHref } from "../../lib/afishaTabs"
import AppStoreBadges from "../../components/AppStoreBadges"
import { useCinemaHref } from "../../hooks/useCinemaHref"
import footerLogo from "../../assets/logo-mk-2.svg"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const footerLinkClass =
    "text-[14px] text-white/55 transition-colors hover:text-white"

const FOOTER_LEGAL = [
    { to: "/purchase-terms", label: "Условия покупки и возврата" },
    { to: "/privacy", label: "Политика конфиденциальности" },
    { to: "/personal-data", label: "Обработка ПД (152-ФЗ)" },
    { to: "/terms", label: "Пользовательское соглашение" },
] as const

function formatHours(hour?: Cinema["hour"]): string | null {
    if (!hour?.start || !hour?.end) return null
    const end = hour.end
    return `${hour.start} – ${end}`
}

function phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, "")}`
}

function mapsHref(geo?: { latitude?: string; longitude?: string }): string | null {
    if (!geo?.latitude || !geo?.longitude) return null
    return `https://yandex.ru/maps/?pt=${geo.longitude},${geo.latitude}&z=16&l=map`
}

function formatTimezone(timezone?: string): string | null {
    if (!timezone) return null
    const match = timezone.match(/^([+-])(\d{2})(\d{2})$/)
    if (!match) return `UTC${timezone}`
    const [, sign, hours, minutes] = match
    return minutes === "00" ? `UTC${sign}${Number(hours)}` : `UTC${sign}${hours}:${minutes}`
}

export default function SiteFooter() {
    const { cinema: selectedCinema, city: selectedCity } = appStore()
    const { requestData } = useRequest()
    const href = useCinemaHref()
    const footerNav = [
        { to: href("/afisha"), label: "Главная", end: true },
        { to: afishaHref("Скоро"), label: "Скоро" },
        { to: afishaHref("Подборки"), label: "Подборки" },
        { to: afishaHref("Акции"), label: "Акции" },
        { to: "/tickets", label: "Билеты" },
        { to: href("/contacts"), label: "Контакты" },
        { to: "/location", label: "Кинотеатры" },
        { to: "/profile", label: "Профиль" },
        { to: "/profile/support", label: "Поддержка" },
        { to: "/profile/about", label: "Справка" },
    ] as const
    const [cinemas, setCinemas] = useState<Cinema[]>([])
    const [cities, setCities] = useState<City[]>([])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            const [cinemaRes, cityRes] = await Promise.all([
                requestData<Cinema[]>({ type: "GET", path: "cinema", disableError: true }),
                requestData<City[]>({ type: "GET", path: "city", disableError: true }),
            ])
            if (cancelled) return
            if (cinemaRes.status === "success" && cinemaRes.data) setCinemas(cinemaRes.data)
            if (cityRes.status === "success" && cityRes.data) setCities(cityRes.data)
        }

        load()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const cinema = useMemo(
        () => cinemas.find(c => c.id === selectedCinema.id) ?? selectedCinema,
        [cinemas, selectedCinema],
    )

    const city = useMemo(
        () => cities.find(c => c.id === selectedCity.id) ?? selectedCity,
        [cities, selectedCity],
    )

    const hours = formatHours(cinema.hour)
    const mapUrl = mapsHref(cinema.geo ?? city.geo)
    const timezone = formatTimezone(city.timezone)
    const year = new Date().getFullYear()

    return (
        <div
            className="mt-auto border-t border-white/10 pb-20 md:pb-0"
            style={{ background: "rgba(52,155,230,0.08)" }}>
            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-10 lg:py-12">
                <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.1fr_0.9fr_1.2fr]">
                    {/* Brand + documents */}
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-4">
                            <Link to={href("/afisha")} className="inline-flex w-fit items-center leading-none opacity-90 transition-opacity hover:opacity-100 lg:-translate-x-6 lg:-translate-y-2">
                                <img src={footerLogo} alt="Мягкий кинотеатр" className="h-[72px] w-auto" />
                            </Link>
                            <div>
                                <p className="text-white text-[15px]" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                                    {cinemaDisplayName(cinema)}
                                </p>
                                <p className="mt-1 text-[13px] text-white/45" style={{ fontFamily: MONO, fontWeight: 600 }}>
                                    {city.name}
                                </p>
                            </div>
                        </div>

                        <div>
                            <p
                                className="mb-4 text-[10px] uppercase tracking-[0.1em] text-white/35"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                Документы
                            </p>
                            <nav className="flex flex-col gap-2.5">
                                {FOOTER_LEGAL.map(({ to, label }) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        className={footerLinkClass}
                                        style={{ fontFamily: BODY, fontWeight: 600 }}>
                                        {label}
                                    </Link>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* Nav */}
                    <div>
                        <p
                            className="mb-4 text-[10px] uppercase tracking-[0.1em] text-white/35"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            Навигация
                        </p>
                        <nav className="flex flex-col gap-2.5">
                            {footerNav.map(({ to, label, ...rest }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    end={"end" in rest ? rest.end : false}
                                    className={footerLinkClass}
                                    style={{ fontFamily: BODY, fontWeight: 600 }}>
                                    {label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    {/* Contacts */}
                    <div>
                        <p
                            className="mb-4 text-[10px] uppercase tracking-[0.1em] text-white/35"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            Контакты · {city.name}
                        </p>
                        <div className="flex flex-col gap-3 text-[14px] text-white/60" style={{ fontFamily: BODY, fontWeight: 500 }}>
                            {cinema.address && (
                                <p>
                                    {mapUrl ? (
                                        <a
                                            href={mapUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="transition-colors hover:text-white/85">
                                            {cinema.address}
                                        </a>
                                    ) : (
                                        cinema.address
                                    )}
                                </p>
                            )}
                            {!cinema.address && mapUrl && (
                                <a
                                    href={mapUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="transition-colors hover:text-white/85">
                                    {city.name} на карте
                                </a>
                            )}
                            {cinema.phone && (
                                <a href={phoneHref(cinema.phone)} className="transition-colors hover:text-white/85" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    {cinema.phone}
                                </a>
                            )}
                            {cinema.contact_email && (
                                <a
                                    href={`mailto:${cinema.contact_email}`}
                                    className="transition-colors hover:text-white/85">
                                    {cinema.contact_email}
                                </a>
                            )}
                            {hours && (
                                <p>
                                    <span className="text-white/40">Режим работы: </span>
                                    <span style={{ fontFamily: MONO, fontWeight: 700 }}>{hours}</span>
                                </p>
                            )}
                            {timezone && (
                                <p>
                                    <span className="text-white/40">Часовой пояс: </span>
                                    <span style={{ fontFamily: MONO, fontWeight: 700 }}>{timezone}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* App badges */}
                <div className="mt-10 border-t border-white/[0.08] pt-6">
                    <p
                        className="mb-4 text-[10px] uppercase tracking-[0.1em] text-white/35"
                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Приложение «Мягкий кинотеатр»
                    </p>
                    <AppStoreBadges variant="compact" />
                </div>

                <div
                    className="mt-8 flex flex-col gap-2 border-t border-white/[0.08] pt-6 text-[12px] text-white/30 sm:flex-row sm:items-center sm:justify-between"
                    style={{ fontFamily: BODY, fontWeight: 500 }}>
                    <p>© {year} ООО Империя игр</p>
                    <a
                        href="https://creativecommons.org/licenses/by/4.0/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-white/45 underline underline-offset-2 transition-colors hover:text-white/70">
                        CC BY 4.0 International
                    </a>
                </div>
            </div>
        </div>
    )
}
